// Animation utility functions
import * as Three from 'three';
import {
  applyPlayModelEffect,
  stopPlayModelForObject,
} from '../../../../AddComponents/AnimationComponent/handlers/animationEffectHandlers';
import { getParticleConfiguredOpacity } from '../../../../../utils/ThreeJsUtils/materialOpacitySnapshot';
import {
  findSceneFromObject,
  syncLightHelperForSceneObject,
} from '../../../../../utils/ThreeJsUtils/lightHelperUtils';
import {
  applyScaleEffectToObject,
  applyPulseScaleToObject,
} from '../../../../gridpagefunction/Text';
import { getTimelinePlaybackTime } from '../../../../AddComponents/utils/timelinePlaybackClock';

// Helper function to ensure userData is extensible
const ensureUserDataExtensible = (obj) => {
  if (!obj) return;

  // Ensure userData exists
  if (!obj.userData) {
    obj.userData = {};
    return;
  }

  // Check if userData is extensible, if not create a new object
  if (!Object.isExtensible(obj.userData)) {
    obj.userData = { ...obj.userData };
  }
};

const syncAnimatedLightHelper = (obj) => {
  if (!obj?.isLight) return;
  const scene = findSceneFromObject(obj);
  if (scene) {
    syncLightHelperForSceneObject(scene, obj);
  }
};

// Helper to detect TransformControls and its gizmo/plane helpers
const isTransformControlsHelper = (obj) => {
  let current = obj;
  while (current) {
    if (
      current.isTransformControls ||
      current.isTransformControlsGizmo ||
      current.isTransformControlsPlane
    ) {
      return true;
    }
    current = current.parent;
  }
  return false;
};

export const isEffectLooping = (effect) =>
  effect?.loop === true || effect?.loop === 'true';

/** Looping clips keep cycling after start — duration is only the cycle length, not a stop time. */
function getEffectProgress(effect, currentTime, isPastEnd) {
  const duration = (effect.endTime || 0) - (effect.startTime || 0);
  const elapsed = Math.max(0, currentTime - effect.startTime);

  if (isEffectLooping(effect) && duration > 0) {
    return Math.min(1, Math.max(0, (elapsed % duration) / duration));
  }
  if (isPastEnd) return 1;
  if (duration <= 0) return 0;
  return Math.min(1, Math.max(0, elapsed / duration));
}

const applyEasing = (progress, easingType) => {
  switch (easingType) {
    case 'Ease-in':
      return progress * progress;
    case 'Ease-out':
      return 1 - Math.pow(1 - progress, 2);
    case 'In-out':
      return progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    case 'None':
    default:
      return progress;
  }
};

const applyMoveEffect = (obj, effect, easedProgress, currentTime) => {
  const movementType = effect.movementType || 'linear';
  const startPos = effect.startPosition || { x: 0, y: 0, z: 0 };
  const endPos = effect.endPosition || { x: 10, y: 0, z: 0 };
  const activeAxes = effect.activeAxes || { x: true, y: false, z: false };

  switch (movementType) {
    case 'linear': {
      // Linear movement
      if (activeAxes.x)
        obj.position.x = startPos.x + (endPos.x - startPos.x) * easedProgress;
      if (activeAxes.y)
        obj.position.y = startPos.y + (endPos.y - startPos.y) * easedProgress;
      if (activeAxes.z)
        obj.position.z = startPos.z + (endPos.z - startPos.z) * easedProgress;
      break;
    }

    case 'circular': {
      const center = effect.circleCenter || { x: 0, y: 0, z: 0 };
      const radius = effect.circleRadius ?? 5;
      const axis = effect.circleAxis || 'z';
      const direction = effect.circleDirection === 'counterclockwise' ? 1 : -1;
      const startAngle = ((effect.circleStartAngle || 0) * Math.PI) / 180;
      const endAngle = ((effect.circleEndAngle || 360) * Math.PI) / 180;
      const revolutions = effect.circleRevolutions || 1;
      if (radius === 0) break;

      const totalAngle =
        endAngle - startAngle + (revolutions - 1) * 2 * Math.PI;
      const currentAngle = startAngle + totalAngle * easedProgress * direction;

      if (axis === 'z') {
        obj.position.x = center.x + radius * Math.cos(currentAngle);
        obj.position.y = center.y + radius * Math.sin(currentAngle);
        obj.position.z = center.z;
      } else if (axis === 'y') {
        obj.position.x = center.x + radius * Math.cos(currentAngle);
        obj.position.z = center.z + radius * Math.sin(currentAngle);
        obj.position.y = center.y;
      } else if (axis === 'x') {
        obj.position.y = center.y + radius * Math.cos(currentAngle);
        obj.position.z = center.z + radius * Math.sin(currentAngle);
        obj.position.x = center.x;
      }
      break;
    }

    case 'bezier': {
      const control1 = effect.bezierControl1 || { x: 5, y: 5, z: 0 };
      const control2 = effect.bezierControl2 || { x: 10, y: -5, z: 0 };

      // Cubic Bezier curve calculation
      const t = easedProgress;
      const t2 = t * t;
      const t3 = t2 * t;
      const mt = 1 - t;
      const mt2 = mt * mt;
      const mt3 = mt2 * mt;

      obj.position.x =
        mt3 * startPos.x +
        3 * mt2 * t * control1.x +
        3 * mt * t2 * control2.x +
        t3 * endPos.x;
      obj.position.y =
        mt3 * startPos.y +
        3 * mt2 * t * control1.y +
        3 * mt * t2 * control2.y +
        t3 * endPos.y;
      obj.position.z =
        mt3 * startPos.z +
        3 * mt2 * t * control1.z +
        3 * mt * t2 * control2.z +
        t3 * endPos.z;
      break;
    }

    case 'sine': {
      const amplitude = effect.sineAmplitude ?? { x: 2, y: 0, z: 0 };
      const frequency = effect.sineFrequency ?? 1;
      const phase = ((effect.sinePhase ?? 0) * Math.PI) / 180;
      const sineAxis = effect.sineAxis ?? 'x';
      const axisAmplitude = amplitude[sineAxis] ?? 0;
      if (axisAmplitude === 0) break;

      const sineValue = Math.sin(currentTime * frequency * 2 * Math.PI + phase);
      const sineOffset = axisAmplitude * sineValue;

      obj.position.x = startPos.x + (endPos.x - startPos.x) * easedProgress;
      obj.position.y = startPos.y + (endPos.y - startPos.y) * easedProgress;
      obj.position.z = startPos.z + (endPos.z - startPos.z) * easedProgress;
      obj.position[sineAxis] += sineOffset;
      break;
    }

    case 'figure8': {
      const radius = effect.figure8Radius ?? 5;
      const axis = effect.figure8Axis ?? 'z';
      const speed = effect.figure8Speed ?? 0.5;

      if (radius === 0) break;

      const t = currentTime * speed * 2 * Math.PI;
      const x = radius * Math.sin(t);
      const y = (radius * Math.sin(2 * t)) / 2;

      if (axis === 'z') {
        obj.position.x = startPos.x + x;
        obj.position.y = startPos.y + y;
        obj.position.z = startPos.z + (endPos.z - startPos.z) * easedProgress;
      } else if (axis === 'y') {
        obj.position.x = startPos.x + x;
        obj.position.z = startPos.z + y;
        obj.position.y = startPos.y + (endPos.y - startPos.y) * easedProgress;
      } else if (axis === 'x') {
        obj.position.y = startPos.y + x;
        obj.position.z = startPos.z + y;
        obj.position.x = startPos.x + (endPos.x - startPos.x) * easedProgress;
      }
      break;
    }
    case 'spiral': {
      const startPos = effect.startPosition || { x: 0, y: 0, z: 0 };
      const radius = effect.spiralRadius ?? 5;
      const height = effect.spiralHeight ?? 5;
      const turns = effect.spiralTurns ?? 2;
      const axis = effect.spiralAxis ?? 'z';
      const direction = effect.spiralDirection === 'counterclockwise' ? 1 : -1;

      // Stop animation if radius and height are both 0
      if (radius === 0 && height === 0) break;

      const angle = easedProgress * turns * 2 * Math.PI * direction;
      const currentRadius = radius * (1 - easedProgress);
      const currentHeight = height * easedProgress;

      if (axis === 'z') {
        obj.position.x = startPos.x + currentRadius * Math.cos(angle);
        obj.position.y = startPos.y + currentRadius * Math.sin(angle);
        obj.position.z = startPos.z + currentHeight;
      } else if (axis === 'y') {
        obj.position.x = startPos.x + currentRadius * Math.cos(angle);
        obj.position.z = startPos.z + currentRadius * Math.sin(angle);
        obj.position.y = startPos.y + currentHeight;
      } else if (axis === 'x') {
        obj.position.y = startPos.y + currentRadius * Math.cos(angle);
        obj.position.z = startPos.z + currentRadius * Math.sin(angle);
        obj.position.x = startPos.x + currentHeight;
      }
      break;
    }

    default:
      // Fallback to linear movement
      if (activeAxes.x)
        obj.position.x = startPos.x + (endPos.x - startPos.x) * easedProgress;
      if (activeAxes.y)
        obj.position.y = startPos.y + (endPos.y - startPos.y) * easedProgress;
      if (activeAxes.z)
        obj.position.z = startPos.z + (endPos.z - startPos.z) * easedProgress;
      break;
      break;

    // case 'spiral': {
    //   const center = effect.spiralCenter || { x: 0, y: 0, z: 0 };
    //   const startRadius = effect.spiralStartRadius || 1;
    //   const endRadius = effect.spiralEndRadius || 5;
    //   const axis = effect.spiralAxis || 'z';
    //   const direction = effect.spiralDirection === 'counterclockwise' ? -1 : 1;
    //   const revolutions = effect.spiralRevolutions || 3;
    //   const pitchHeight = effect.spiralPitchHeight || 5;

    //   const currentRadius =
    //     startRadius + (endRadius - startRadius) * easedProgress;
    //   const currentAngle =
    //     revolutions * 2 * Math.PI * easedProgress * direction;
    //   const currentHeight = pitchHeight * easedProgress;

    //   if (axis === 'z') {
    //     obj.position.x = center.x + currentRadius * Math.cos(currentAngle);
    //     obj.position.y = center.y + currentRadius * Math.sin(currentAngle);
    //     obj.position.z = center.z + currentHeight;
    //   } else if (axis === 'y') {
    //     obj.position.x = center.x + currentRadius * Math.cos(currentAngle);
    //     obj.position.z = center.z + currentHeight;
    //     obj.position.y = center.y + currentRadius * Math.sin(currentAngle);
    //   } else if (axis === 'x') {
    //     obj.position.y = center.y + currentRadius * Math.cos(currentAngle);
    //     obj.position.z = center.z + currentRadius * Math.sin(currentAngle);
    //     obj.position.x = center.x + currentHeight;
    //   }
    //   break;
    // }
  }
};

// Enhanced animation effects - supporting all effects from AnimationTimelinePanel
const applyAdditiveAnimationEffect = (
  obj,
  effect,
  easedProgress,
  newTime,
  basePosition
) => {
  switch (effect.effectType) {
    case 'shake': {
      const shakeIntensity = effect.shakeIntensity ?? 0.2;
      const shakeFrequency = effect.shakeFrequency ?? 10;
      const shakeAxes = effect.shakeAxes ?? { x: true, y: true, z: false };
      const shakeDecay = effect.shakeDecay ?? 0.95;
      const shakeType = effect.shakeType ?? 'random';

      const decayFactor = Math.pow(shakeDecay, easedProgress * 10);
      const intensity = shakeIntensity * decayFactor;
      // Treat shakeFrequency as Hz (cycles/sec) and convert to radians/sec
      const time = newTime * shakeFrequency * 2 * Math.PI;

      let shakeX = 0,
        shakeY = 0,
        shakeZ = 0;
      if (shakeType === 'sine') {
        shakeX = Math.sin(time) * intensity;
        shakeY = Math.sin(time + Math.PI / 2) * intensity;
        shakeZ = Math.sin(time + Math.PI) * intensity;
      } else if (shakeType === 'random') {
        // Deterministic pseudo-random based on time so frequency controls change rate
        shakeX = Math.sin(time * 1.37) * intensity;
        shakeY = Math.sin(time * 1.73 + 1.0) * intensity;
        shakeZ = Math.sin(time * 2.11 + 2.0) * intensity;
      }

      // Add shake to base position
      obj.position.x = basePosition.x + (shakeAxes.x ? shakeX : 0);
      obj.position.y = basePosition.y + (shakeAxes.y ? shakeY : 0);
      obj.position.z = basePosition.z + (shakeAxes.z ? shakeZ : 0);
      break;
    }
    case 'wiggle': {
      const wiggleAmount = effect.wiggleAmount || { x: 5, y: 5, z: 0 };
      const wiggleSpeed = effect.wiggleSpeed || { x: 2, y: 3, z: 0 };
      const wiggleType = effect.wiggleType || 'sine';
      const wigglePhase = effect.wigglePhase || { x: 0, y: 90, z: 0 };

      const time = newTime - effect.startTime;
      let offsetX = 0,
        offsetY = 0,
        offsetZ = 0;

      if (wiggleType === 'sine') {
        offsetX =
          wiggleAmount.x *
          Math.sin(time * wiggleSpeed.x + (wigglePhase.x * Math.PI) / 180);
        offsetY =
          wiggleAmount.y *
          Math.sin(time * wiggleSpeed.y + (wigglePhase.y * Math.PI) / 180);
        offsetZ =
          wiggleAmount.z *
          Math.sin(time * wiggleSpeed.z + (wigglePhase.z * Math.PI) / 180);
      } else if (wiggleType === 'noise') {
        offsetX = wiggleAmount.x * (Math.random() - 0.5) * 2;
        offsetY = wiggleAmount.y * (Math.random() - 0.5) * 2;
        offsetZ = wiggleAmount.z * (Math.random() - 0.5) * 2;
      }

      // Add wiggle to base position
      obj.position.x = basePosition.x + offsetX;
      obj.position.y = basePosition.y + offsetY;
      obj.position.z = basePosition.z + offsetZ;
      break;
    }
    case 'bounce': {
      const bounceHeight =
        effect.bounceHeight === '' || effect.bounceHeight == null
          ? 0
          : Number(effect.bounceHeight);
      const bounceCount =
        effect.bounceCount === '' || effect.bounceCount == null
          ? 0
          : Number(effect.bounceCount);

      // Stop animation if height or count is 0 or empty
      if (bounceHeight === 0 || bounceCount === 0) break;

      const bounceDecay = effect.bounceDecay ?? 0.8;
      // ... rest of bounce code unchanged
      const rawAxis = effect.bounceAxis ?? 'y';

      // Support both string ("x"|"y"|"z") and axes object ({x:boolean,y:boolean,z:boolean})
      let axis = 'y';
      if (typeof rawAxis === 'string') {
        axis = rawAxis;
      } else if (typeof rawAxis === 'object' && rawAxis !== null) {
        if (rawAxis.x) axis = 'x';
        else if (rawAxis.y) axis = 'y';
        else if (rawAxis.z) axis = 'z';
      }

      // Produce an up-then-return profile per bounce with decay
      // progressWithinBounce goes 0 -> 1 repeatedly across bounceCount cycles
      const progressWithinBounce = (easedProgress * bounceCount) % 1;
      const bounceIndex = Math.floor(easedProgress * bounceCount);
      const decayFactor = Math.pow(bounceDecay, bounceIndex);
      // Triangle wave: up then back down to base (0 -> 1 -> 0)
      const triangle =
        progressWithinBounce <= 0.5
          ? progressWithinBounce * 2
          : 2 - progressWithinBounce * 2;
      const bounceValue = bounceHeight * decayFactor * triangle;

      if (axis === 'x') obj.position.x = basePosition.x + bounceValue;
      else if (axis === 'y') obj.position.y = basePosition.y + bounceValue;
      else if (axis === 'z') obj.position.z = basePosition.z + bounceValue;
      break;
    }
    // case 'orbit': {
    //   const orbitRadius = effect.orbitRadius || 5;
    //   const a = effect.orbitSpeed || 90;
    //   const orbitAxis = effect.orbitAxis || 'z';
    //   const orbitCenter = effect.orbitCenter || { x: 0, y: 0, z: 0 };
    //   const orbitDirection = effect.orbitDirection || 'clockwise';

    //   const angle =
    //     (orbitDirection === 'clockwise' ? -1 : 1) *
    //     ((orbitSpeed * Math.PI) / 180) *
    //     (newTime - effect.startTime);

    //   let orbitX = 0,
    //     orbitY = 0,
    //     orbitZ = 0;
    //   if (orbitAxis === 'z') {
    //     orbitX = orbitRadius * Math.cos(angle);
    //     orbitY = orbitRadius * Math.sin(angle);
    //   } else if (orbitAxis === 'y') {
    //     orbitX = orbitRadius * Math.cos(angle);
    //     orbitZ = orbitRadius * Math.sin(angle);
    //   } else if (orbitAxis === 'x') {
    //     orbitY = orbitRadius * Math.cos(angle);
    //     orbitZ = orbitRadius * Math.sin(angle);
    //   }

    //   // Add orbit to base position
    //   obj.position.x = basePosition.x + orbitX;
    //   obj.position.y = basePosition.y + orbitY;
    //   obj.position.z = basePosition.z + orbitZ;
    //   break;
    // }

    case 'orbit': {
      const orbitRadius = effect.orbitRadius ?? 5;
      const orbitSpeed = effect.orbitSpeed ?? 90;
      const orbitAxis = effect.orbitAxis ?? 'z';
      const orbitDirection = effect.orbitDirection ?? 'clockwise';
      const orbitTilt = ((effect.orbitTilt ?? 0) * Math.PI) / 180;

      const orbitCenter = effect.orbitCenter ?? { x: 0, y: 0, z: 0 };

      const angle =
        (orbitDirection === 'clockwise' ? -1 : 1) *
        ((orbitSpeed * Math.PI) / 180) *
        (newTime - effect.startTime);

      let orbitX = 0,
        orbitY = 0,
        orbitZ = 0;

      // --- Base orbit ---
      if (orbitAxis === 'z') {
        orbitX = orbitRadius * Math.cos(angle);
        orbitY = orbitRadius * Math.sin(angle);

        // Tilt around X axis
        const y = orbitY * Math.cos(orbitTilt);
        const z = orbitY * Math.sin(orbitTilt);
        orbitY = y;
        orbitZ = z;
      } else if (orbitAxis === 'y') {
        orbitX = orbitRadius * Math.cos(angle);
        orbitZ = orbitRadius * Math.sin(angle);

        // Tilt around X axis
        const z = orbitZ * Math.cos(orbitTilt);
        const y = orbitZ * Math.sin(orbitTilt);
        orbitZ = z;
        orbitY = y;
      } else if (orbitAxis === 'x') {
        orbitY = orbitRadius * Math.cos(angle);
        orbitZ = orbitRadius * Math.sin(angle);

        // Tilt around Y axis
        const z = orbitZ * Math.cos(orbitTilt);
        const x = orbitZ * Math.sin(orbitTilt);
        orbitZ = z;
        orbitX = x;
      }

      // --- Orbit center in world space ---
      const centerX = basePosition.x + orbitCenter.x;
      const centerY = basePosition.y + orbitCenter.y;
      const centerZ = basePosition.z + orbitCenter.z;

      // --- Apply orbit ---
      obj.position.x = centerX + orbitX;
      obj.position.y = centerY + orbitY;
      obj.position.z = centerZ + orbitZ;

      break;
    }

    case 'spring': {
      const springTension = effect.springTension || 170;
      const springFriction = effect.springFriction || 26;
      const springMass = effect.springMass || 1;
      const springRestPosition = effect.springRestPosition || {
        x: 0,
        y: 0,
        z: 0,
      };

      const damping =
        springFriction / (2 * Math.sqrt(springMass * springTension));
      const frequency = Math.sqrt(springTension / springMass) / (2 * Math.PI);

      if (damping < 1) {
        const dampedFreq = frequency * Math.sqrt(1 - damping * damping);
        const amplitude = Math.exp(
          -damping * frequency * 2 * Math.PI * easedProgress
        );
        const springValue =
          amplitude * Math.cos(2 * Math.PI * dampedFreq * easedProgress);

        // Add spring to base position
        obj.position.x =
          basePosition.x +
          (springRestPosition.x - basePosition.x) * springValue;
        obj.position.y =
          basePosition.y +
          (springRestPosition.y - basePosition.y) * springValue;
        obj.position.z =
          basePosition.z +
          (springRestPosition.z - basePosition.z) * springValue;
      }
      break;
    }
  }
};

const INDEPENDENT_TRANSFORM_CLIPS = new Set(['scale', 'rotate', 'move']);

function hasActiveClipOfType(animations, currentTime, effectType) {
  return animations.some(
    (clip) =>
      clip.effectType === effectType &&
      currentTime >= clip.startTime &&
      currentTime <= clip.endTime
  );
}

/** Finished transform clips must not override the next clip of the same type. */
function shouldSkipFinishedTransformClip(
  effect,
  animations,
  currentTime,
  isPastEnd
) {
  if (isEffectLooping(effect)) return false;
  return (
    isPastEnd &&
    INDEPENDENT_TRANSFORM_CLIPS.has(effect.effectType) &&
    hasActiveClipOfType(animations, currentTime, effect.effectType)
  );
}

function sortAnimationsForApply(animations) {
  return [...animations].sort(
    (a, b) =>
      a.startTime - b.startTime ||
      a.endTime - b.endTime ||
      String(a.id || '').localeCompare(String(b.id || ''))
  );
}

function resetRotationTracking(obj) {
  if (!obj?.userData?.originalRotation) return;
  ensureUserDataExtensible(obj);
  const ox = obj.userData.originalRotation.x;
  const oy = obj.userData.originalRotation.y;
  const oz = obj.userData.originalRotation.z;
  // History restore may leave plain {x,y,z} — always ensure a live Euler.
  if (typeof obj.userData.prevRotation?.set !== 'function') {
    obj.userData.prevRotation = new Three.Euler(ox, oy, oz);
    return;
  }
  obj.userData.prevRotation.set(ox, oy, oz);
}

/** Apply absolute startScale→endScale from the effect panel (e.g. 2,2,2 → 3,3,3). */
function applyScaleEffect(obj, effect, easedProgress) {
  applyScaleEffectToObject(obj, effect, easedProgress);
}

const applyAnimationEffect = (obj, effect, easedProgress, newTime, isStatic = false) => {
  switch (effect.effectType) {
    case 'move':
      // Move effects are handled in the first pass
      break;
    case 'rotate': {
      // Support both old and new property naming conventions
      const startRot = effect.startRotation || { x: 0, y: 0, z: 0 };
      const endRot = effect.endRotation || { x: 0, y: 0, z: 360 };

      const activeAxes = effect.activeAxes ||
        effect.rotationAxis || { x: false, y: false, z: true };

      const rotationDirection = effect.rotationDirection || 'clockwise';
      const pivotPoint = effect.pivotPoint || { x: 0, y: 0, z: 0 };

      const degToRad = Math.PI / 180;
      const direction = rotationDirection === 'clockwise' ? -1 : 1;

      // Ensure userData is extensible before modifying
      ensureUserDataExtensible(obj);

      // Store previous rotation on object (FIRST TIME SAFE).
      // History restore may leave plain {x,y,z} without .set — recreate Euler.
      if (typeof obj.userData.prevRotation?.set !== 'function') {
        obj.userData.prevRotation = new Three.Euler(
          obj.rotation.x,
          obj.rotation.y,
          obj.rotation.z
        );
      }

      // === CALCULATE TARGET ROTATION (ABSOLUTE) ===
      const targetRotX = activeAxes.x
        ? (startRot.x + (endRot.x - startRot.x) * easedProgress) *
        degToRad *
        direction
        : obj.rotation.x;

      const targetRotY = activeAxes.y
        ? (startRot.y + (endRot.y - startRot.y) * easedProgress) *
        degToRad *
        direction
        : obj.rotation.y;

      const targetRotZ = activeAxes.z
        ? (startRot.z + (endRot.z - startRot.z) * easedProgress) *
        degToRad *
        direction
        : obj.rotation.z;

      // === DELTA ROTATION (THIS IS THE FIX) ===
      const deltaX = targetRotX - obj.userData.prevRotation.x;
      const deltaY = targetRotY - obj.userData.prevRotation.y;
      const deltaZ = targetRotZ - obj.userData.prevRotation.z;

      // Apply rotation to object
      obj.rotation.set(targetRotX, targetRotY, targetRotZ);

      // === HANDLE PIVOT ROTATION (DELTA BASED) ===
      if (pivotPoint.x !== 0 || pivotPoint.y !== 0 || pivotPoint.z !== 0) {
        const pivot = new Three.Vector3(
          pivotPoint.x,
          pivotPoint.y,
          pivotPoint.z
        );

        // Move relative to pivot
        obj.position.sub(pivot);

        // Rotate POSITION by DELTA
        if (activeAxes.x && deltaX !== 0) {
          obj.position.applyAxisAngle(new Three.Vector3(1, 0, 0), deltaX);
        }

        if (activeAxes.y && deltaY !== 0) {
          obj.position.applyAxisAngle(new Three.Vector3(0, 1, 0), deltaY);
        }

        if (activeAxes.z && deltaZ !== 0) {
          obj.position.applyAxisAngle(new Three.Vector3(0, 0, 1), deltaZ);
        }

        // Move back
        obj.position.add(pivot);
      }

      // Save rotation for next frame
      obj.userData.prevRotation.set(targetRotX, targetRotY, targetRotZ);

      break;
    }
    case 'scale': {
      applyScaleEffect(obj, effect, easedProgress);
      break;
    }

    case 'fadein': {
      const startOpacity =
        effect.startOpacity !== undefined ? effect.startOpacity : 0;

      // Default endOpacity to 1.0 or the object's configured opacity
      const configuredOp = getParticleConfiguredOpacity(obj);
      const endOpacity =
        effect.endOpacity !== undefined
          ? effect.endOpacity
          : configuredOp !== undefined
            ? configuredOp
            : 1;

      const currentOpacity =
        startOpacity + (endOpacity - startOpacity) * easedProgress;

      if (obj.material) {
        obj.material.transparent = true;
        obj.material.opacity = currentOpacity;
        obj.material.needsUpdate = true;

        // Handle water shader alpha uniform
        if (obj.material.uniforms && obj.material.uniforms.alpha) {
          obj.material.uniforms.alpha.value = currentOpacity;
        }
      }

      // Always traverse children to handle complex objects like terrain with rocks
      obj.traverse((child) => {
        if (child.material) {
          const materials = Array.isArray(child.material)
            ? child.material
            : [child.material];

          materials.forEach((material) => {
            material.transparent = true;
            material.opacity = currentOpacity;
            material.needsUpdate = true;

            // Handle water shader alpha uniform in children
            if (material.uniforms && material.uniforms.alpha) {
              material.uniforms.alpha.value = currentOpacity;
            }
          });
        }
      });

      break;
    }

    case 'fadeout': {
      // Default startOpacity to 1.0 or the object's configured opacity
      const configuredOp = getParticleConfiguredOpacity(obj);
      const startOpacity =
        effect.startOpacity !== undefined
          ? effect.startOpacity
          : configuredOp !== undefined
            ? configuredOp
            : 1;

      const endOpacity =
        effect.endOpacity !== undefined ? effect.endOpacity : 0;

      const currentOpacity =
        startOpacity + (endOpacity - startOpacity) * easedProgress;

      if (obj.material) {
        obj.material.transparent = true;
        obj.material.opacity = currentOpacity;
        obj.material.needsUpdate = true;

        // Handle water shader alpha uniform
        if (obj.material.uniforms && obj.material.uniforms.alpha) {
          obj.material.uniforms.alpha.value = currentOpacity;
        }
      }

      // Always traverse children to handle complex objects like terrain with rocks
      obj.traverse((child) => {
        if (child.material) {
          const materials = Array.isArray(child.material)
            ? child.material
            : [child.material];

          materials.forEach((material) => {
            material.transparent = true;
            material.opacity = currentOpacity;
            material.needsUpdate = true;

            // Handle water shader alpha uniform in children
            if (material.uniforms && material.uniforms.alpha) {
              material.uniforms.alpha.value = currentOpacity;
            }
          });
        }
      });

      break;
    }

    case 'playModel': {
      if (!obj) break;
      ensureUserDataExtensible(obj);
      applyPlayModelEffect(obj, effect, easedProgress, newTime, isStatic);
      break;
    }

    case 'pulse': {
      const pulseScale = effect.pulseScale || 1.5;
      const pulseRate = effect.pulseRate || effect.pulseSpeed || 2;
      const pulseCount = effect.pulseCount || 1;
      const pulseSmooth =
        effect.pulseSmooth !== undefined ? effect.pulseSmooth : true;
      const pulseAxis = effect.pulseAxis || 'uniform';

      // Calculate pulse progress with count consideration
      const pulseProgress = Math.sin(
        easedProgress * Math.PI * 1 * pulseRate * pulseCount
      );
      const scaleFactor = pulseSmooth
        ? 1 + (pulseScale - 1) * (pulseProgress * 0.5 + 0.5)
        : 1 + (pulseScale - 1) * Math.abs(pulseProgress);

      // Handle axis configuration - support both object and string formats
      applyPulseScaleToObject(obj, scaleFactor, pulseAxis);
      break;
    }

    case 'bounce': {
      const bounceHeight = effect.bounceHeight || 5;
      const bounceCount = effect.bounceCount || 3;
      const bounceDecay = effect.bounceDecay || 0.8;
      const rawAxis = effect.bounceAxis ?? 'y';

      // Support both string ("x"|"y"|"z") and axes object ({x:boolean,y:boolean,z:boolean})
      let axis = 'y';
      if (typeof rawAxis === 'string') {
        axis = rawAxis;
      } else if (typeof rawAxis === 'object' && rawAxis !== null) {
        if (rawAxis.x) axis = 'x';
        else if (rawAxis.y) axis = 'y';
        else if (rawAxis.z) axis = 'z';
      }

      // Get the original position from userData or use current position as fallback
      const originalPosition =
        obj.userData.originalPosition || obj.position.clone();

      // Produce an up-then-return profile per bounce with decay
      // progressWithinBounce goes 0 -> 1 repeatedly across bounceCount cycles
      const progressWithinBounce = (easedProgress * bounceCount) % 1;
      const bounceIndex = Math.floor(easedProgress * bounceCount);
      const decayFactor = Math.pow(bounceDecay, bounceIndex);
      // Triangle wave: up then back down to base (0 -> 1 -> 0)
      const triangle =
        progressWithinBounce <= 0.5
          ? progressWithinBounce * 2
          : 2 - progressWithinBounce * 2;
      const bounceValue = bounceHeight * decayFactor * triangle;

      // Apply bounce as standalone effect - goes up and comes back down to original position
      if (axis === 'x') obj.position.x = originalPosition.x + bounceValue;
      else if (axis === 'y') obj.position.y = originalPosition.y + bounceValue;
      else if (axis === 'z') obj.position.z = originalPosition.z + bounceValue;
      break;
    }
    default:
      console.warn(`Unknown animation effect type: ${effect.effectType}`);
  }
};

const applyAnimationsToObject = (
  obj,
  animations,
  animationIdRef = null,
  startOffset = 0, // seconds to add to elapsed time (used for resume)
  playbackGenerationRef = null
) => {
  if (!obj || !animations || animations.length === 0) return;

  // Never animate TransformControls or its helpers
  if (isTransformControlsHelper(obj)) return;

  const playbackGeneration = playbackGenerationRef?.current ?? null;

  // Ensure userData exists and is extensible
  ensureUserDataExtensible(obj);

  // Baselines are synced from captured rest pose by the timeline panel before this runs.
  if (!obj.userData.originalPosition) {
    obj.userData.originalPosition = obj.position.clone();
  }
  if (!obj.userData.originalRotation) {
    obj.userData.originalRotation = obj.rotation.clone();
  }
  if (!obj.userData.originalScale) {
    obj.userData.originalScale = obj.scale.clone();
  }

  if (!obj.userData.originalMaterials) {
    const originalMaterials = [];
    obj.traverse((child) => {
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach((mat) => {
            originalMaterials.push({
              mat,
              opacity: mat.opacity,
              transparent: mat.transparent
            });
          });
        } else {
          originalMaterials.push({
            mat: child.material,
            opacity: child.material.opacity,
            transparent: child.material.transparent
          });
        }
      }
    });
    obj.userData.originalMaterials = originalMaterials;
  }

  // Initialize cumulative transforms at frame start
  if (!obj.userData.framePosition) {
    obj.userData.framePosition = obj.position.clone();
  }
  if (!obj.userData.frameRotation) {
    obj.userData.frameRotation = obj.rotation.clone();
  }
  if (!obj.userData.frameScale) {
    obj.userData.frameScale = obj.scale.clone();
  }

  // Start animation loop
  // subtract startOffset from startTime so that currentTime includes it
  const startTime = Date.now() - startOffset * 1000;
  let currentAnimationId = null;

  const animate = () => {
    if (
      playbackGenerationRef &&
      playbackGenerationRef.current !== playbackGeneration
    ) {
      if (animationIdRef) animationIdRef.current = null;
      return;
    }

    const timelineActive =
      typeof window !== 'undefined' && window.__timelinePlaybackActive;

    // Timeline pause/stop: freeze this object's pose. Do not keep looping
    // on wall-clock time, and do not snap back to the rest pose.
    if (playbackGenerationRef && !timelineActive) {
      if (animationIdRef) animationIdRef.current = null;
      return;
    }

    // Prefer shared timeline clock while timeline transport is active
    const currentTime = timelineActive
      ? getTimelinePlaybackTime()
      : (Date.now() - startTime) / 1000;

    // Reset frame transforms to original at start of frame
    obj.userData.framePosition.copy(obj.userData.originalPosition);
    obj.userData.frameRotation.copy(obj.userData.originalRotation);
    obj.userData.frameScale.copy(obj.userData.originalScale);

    // Reset object to original state before applying animations
    obj.position.copy(obj.userData.originalPosition);
    obj.rotation.copy(obj.userData.originalRotation);
    obj.scale.copy(obj.userData.originalScale);
    resetRotationTracking(obj);

    const sortedAnimations = sortAnimationsForApply(animations);

    // Reset opacities to original state
    const hasOpacityAnimation = animations.some(e => ['fadein', 'fadeout', 'fade'].includes(e.effectType));
    if (hasOpacityAnimation) {
      if (!obj.userData.cachedMaterialInfo) {
        const info = [];
        obj.traverse((child) => {
          if (child.material) {
            const materials = Array.isArray(child.material) ? child.material : [child.material];
            materials.forEach((mat) => {
              info.push({
                mat,
                originalOpacity: mat.opacity,
                originalTransparent: mat.transparent,
                hasAlphaUniform: !!(mat.uniforms && mat.uniforms.alpha)
              });
            });
          }
        });
        obj.userData.cachedMaterialInfo = info;
      }

      obj.userData.cachedMaterialInfo.forEach((item) => {
        const { mat, originalOpacity, originalTransparent, hasAlphaUniform } = item;
        const particleOp = getParticleConfiguredOpacity(obj);
        const targetOpacity =
          particleOp !== undefined ? particleOp : originalOpacity;

        if (mat.opacity !== targetOpacity || mat.transparent !== true) {
          mat.opacity = targetOpacity;
          mat.transparent = true;
          mat.needsUpdate = true;

          if (hasAlphaUniform) {
            mat.uniforms.alpha.value = targetOpacity;
          }
        }
      });
    }

    // Apply each animation effect (sorted so later clips win on the same property)
    sortedAnimations.forEach((effect) => {
      // Process all effects that have already started
      const hasStarted = currentTime >= effect.startTime;
      const looping = isEffectLooping(effect);
      // Looping clips never "finish" — endTime is only the cycle length
      const isPastEnd = !looping && currentTime > effect.endTime;

      if (hasStarted) {
        // Skip transient oscillation effects after they end (otherwise they keep shaking/wiggling)
        if (isPastEnd && ['shake', 'wiggle'].includes(effect.effectType)) {
          return;
        }
        if (
          shouldSkipFinishedTransformClip(
            effect,
            animations,
            currentTime,
            isPastEnd
          )
        ) {
          return;
        }

        const progress = getEffectProgress(effect, currentTime, isPastEnd);
        const easedProgress = applyEasing(progress, effect.easing || 'None');

        // Use clamped time for effects that use absolute time (sine, figure8, orbit)
        const effectiveTime = isPastEnd ? effect.endTime : currentTime;

        // Store base position for additive effects - use current frame position (accumulated)
        const basePosition = obj.userData.framePosition.clone();

        // First pass: handle move effects
        if (effect.effectType === 'move') {
          applyMoveEffect(obj, effect, easedProgress, effectiveTime);
          // Accumulate position changes
          obj.userData.framePosition.copy(obj.position);
        }

        // Second pass: apply additive effects (shake, wiggle, orbit, spring)
        if (['shake', 'wiggle', 'orbit', 'spring'].includes(effect.effectType)) {
          applyAdditiveAnimationEffect(
            obj,
            effect,
            easedProgress,
            effectiveTime,
            basePosition
          );
          // Accumulate position changes
          obj.userData.framePosition.copy(obj.position);
        }

        // Third pass: apply other effects (rotate, scale, fade, pulse, bounce)
        if (
          [
            'rotate',
            'scale',
            'fade',
            'fadein',
            'fadeout',
            'pulse',
            'bounce',
            'playModel',
          ].includes(effect.effectType)
        ) {
          applyAnimationEffect(obj, effect, easedProgress, effectiveTime, false);
          if (['rotate', 'pulse'].includes(effect.effectType)) {
            obj.userData.frameScale.copy(obj.scale);
            obj.userData.frameRotation.copy(obj.rotation);
          }
        }

        // Handle auto-rotation if enabled
        if (effect.autoRotate) {
          const lookAtTarget = effect.lookAtTarget || { x: 0, y: 0, z: 0 };
          obj.lookAt(lookAtTarget.x, lookAtTarget.y, lookAtTarget.z);
          obj.userData.frameRotation.copy(obj.rotation);
        }
      }
    });

    const hasActivePlayModel = animations.some((effect) => {
      if (effect.effectType !== 'playModel') return false;
      if (currentTime < effect.startTime) return false;
      if (isEffectLooping(effect)) return true;
      return currentTime <= effect.endTime;
    });
    // While shared timeline is still running, hold the last playModel pose —
    // do not reset to bind pose before the global preview timeline ends.
    if (
      !hasActivePlayModel &&
      !(typeof window !== 'undefined' && window.__timelinePlaybackActive)
    ) {
      stopPlayModelForObject(obj);
    }

    syncAnimatedLightHelper(obj);

    // Continue while the timeline transport is driving time (incl. reverse),
    // or while any effect still has remaining duration at normal speed.
    const hasActiveEffects = animations.some((effect) => {
      if (currentTime < effect.startTime) return false;
      if (isEffectLooping(effect)) return true;
      return currentTime < effect.endTime;
    });

    if (timelineActive || hasActiveEffects) {
      currentAnimationId = requestAnimationFrame(animate);
      // Update the reference if provided
      if (animationIdRef) {
        animationIdRef.current = currentAnimationId;
      }
    } else {
      // Animation completed - reset object to original state
      if (obj.userData.originalPosition) {
        obj.position.copy(obj.userData.originalPosition);
      }
      if (obj.userData.originalRotation) {
        obj.rotation.copy(obj.userData.originalRotation);
      }
      if (obj.userData.originalScale) {
        obj.scale.copy(obj.userData.originalScale);
      }
      syncAnimatedLightHelper(obj);
      stopPlayModelForObject(obj);
      // Reset opacity to original state
      if (obj.userData.originalMaterials) {
        obj.userData.originalMaterials.forEach(({ mat, opacity, transparent }) => {
          // Use userData.opacity if available, otherwise original opacity
          const configuredOp = getParticleConfiguredOpacity(obj);
          const targetOpacity =
            configuredOp !== undefined ? configuredOp : opacity;

          if (mat.opacity !== targetOpacity || mat.transparent !== transparent) {
            mat.opacity = targetOpacity;
            mat.transparent = transparent;
            mat.needsUpdate = true;

            // Handle water shader alpha uniform
            if (mat.uniforms && mat.uniforms.alpha) {
              mat.uniforms.alpha.value = targetOpacity;
            }
          }
        });
      } else if (obj.deleteType !== 'particle') {
        // Fallback — never force particle subtrees to opaque 1 (clouds/foliage use many materials)
        obj.traverse((child) => {
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => {
                material.opacity = 1;
                material.needsUpdate = true;
              });
            } else {
              child.material.opacity = 1;
              child.material.needsUpdate = true;
            }
          }
        });
      }
      if (animationIdRef) {
        animationIdRef.current = null;
      }
    }
  };

  // Start the animation
  currentAnimationId = requestAnimationFrame(animate);
  if (animationIdRef) {
    animationIdRef.current = currentAnimationId;
  }

  return currentAnimationId;
};

// Applies static animation state at a specific time without starting a loop.
const applyStaticAnimations = (obj, animations, currentTime) => {
  if (!obj || !animations || animations.length === 0) return;

  // Never animate TransformControls or its helpers
  if (isTransformControlsHelper(obj)) return;

  // Ensure userData exists and is extensible
  ensureUserDataExtensible(obj);

  // Baselines are synced from captured rest pose by the timeline panel before this runs.
  if (!obj.userData.originalPosition) {
    obj.userData.originalPosition = obj.position.clone();
  }
  if (!obj.userData.originalRotation) {
    obj.userData.originalRotation = obj.rotation.clone();
  }
  if (!obj.userData.originalScale) {
    obj.userData.originalScale = obj.scale.clone();
  }

  if (!obj.userData.originalMaterials) {
    const originalMaterials = [];
    obj.traverse((child) => {
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach((mat) => {
            originalMaterials.push({
              mat,
              opacity: mat.opacity,
              transparent: mat.transparent
            });
          });
        } else {
          originalMaterials.push({
            mat: child.material,
            opacity: child.material.opacity,
            transparent: child.material.transparent
          });
        }
      }
    });
    obj.userData.originalMaterials = originalMaterials;
  }

  // Initialize frame transforms (cumulative)
  if (!obj.userData.framePosition) {
    obj.userData.framePosition = obj.userData.originalPosition.clone();
  }
  if (!obj.userData.frameRotation) {
    obj.userData.frameRotation = obj.userData.originalRotation.clone();
  }
  if (!obj.userData.frameScale) {
    obj.userData.frameScale = obj.userData.originalScale.clone();
  }

  // Reset object to original state before applying animations
  obj.position.copy(obj.userData.originalPosition);
  obj.rotation.copy(obj.userData.originalRotation);
  obj.scale.copy(obj.userData.originalScale);
  resetRotationTracking(obj);

  // Reset frame transforms to original
  obj.userData.framePosition.copy(obj.userData.originalPosition);
  obj.userData.frameRotation.copy(obj.userData.originalRotation);
  obj.userData.frameScale.copy(obj.userData.originalScale);

  const sortedAnimations = sortAnimationsForApply(animations);

  // Do not call SkinnedMesh.pose() here — it detaches vertices from animated bones.

  // Reset opacities to original state
  const hasOpacityAnimation = animations.some(e => ['fadein', 'fadeout', 'fade'].includes(e.effectType));
  if (hasOpacityAnimation && obj.userData.originalMaterials) {
    obj.userData.originalMaterials.forEach(({ mat, opacity, transparent }) => {
      // Use userData.opacity if available, otherwise original opacity
      const configuredOp = getParticleConfiguredOpacity(obj);
      const targetOpacity =
        configuredOp !== undefined ? configuredOp : opacity;

      if (mat.opacity !== targetOpacity || mat.transparent !== transparent) {
        mat.opacity = targetOpacity;
        mat.transparent = transparent;
        mat.needsUpdate = true;

        // Handle water shader alpha uniform
        if (mat.uniforms && mat.uniforms.alpha) {
          mat.uniforms.alpha.value = targetOpacity;
        }
      }
    });
  }

  // Apply each animation effect at the specified currentTime
  sortedAnimations.forEach((effect) => {
    // Process all effects that have already started at this scrubbing time
    const hasStarted = currentTime >= effect.startTime;
    const looping = isEffectLooping(effect);
    const isPastEnd = !looping && currentTime > effect.endTime;

    if (hasStarted) {
      // Skip transient oscillation effects after they end
      if (isPastEnd && ['shake', 'wiggle'].includes(effect.effectType)) {
        return;
      }
      if (
        shouldSkipFinishedTransformClip(
          effect,
          animations,
          currentTime,
          isPastEnd
        )
      ) {
        return;
      }

      const progress = getEffectProgress(effect, currentTime, isPastEnd);
      const easedProgress = applyEasing(progress, effect.easing || 'None');
      const effectiveTime = isPastEnd ? effect.endTime : currentTime;
      const basePosition = obj.userData.framePosition.clone();

      // Apply effects sequentially matching applyAnimationsToObject loop
      if (effect.effectType === 'move') {
        applyMoveEffect(obj, effect, easedProgress, effectiveTime);
        // Accumulate position changes
        obj.userData.framePosition.copy(obj.position);
      }

      if (['shake', 'wiggle', 'orbit', 'spring'].includes(effect.effectType)) {
        applyAdditiveAnimationEffect(obj, effect, easedProgress, effectiveTime, basePosition);
        // Accumulate position changes
        obj.userData.framePosition.copy(obj.position);
      }

      if (['rotate', 'scale', 'fade', 'fadein', 'fadeout', 'pulse', 'bounce', 'playModel'].includes(effect.effectType)) {
        applyAnimationEffect(obj, effect, easedProgress, effectiveTime, true);
        if (['rotate', 'pulse'].includes(effect.effectType)) {
          obj.userData.frameScale.copy(obj.scale);
          obj.userData.frameRotation.copy(obj.rotation);
        }
      }

      if (effect.autoRotate) {
        const lookAtTarget = effect.lookAtTarget || { x: 0, y: 0, z: 0 };
        obj.lookAt(lookAtTarget.x, lookAtTarget.y, lookAtTarget.z);
        obj.userData.frameRotation.copy(obj.rotation);
      }
    }
  });

  syncAnimatedLightHelper(obj);
};

export {
  applyAnimationsToObject,
  applyStaticAnimations,
  applyEasing,
  applyMoveEffect,
  applyAdditiveAnimationEffect,
  applyAnimationEffect,
};
